import { getMatrixEntry } from './commissionMatrixUtils';

/**
 * Calculate final price from net price adding fees and taxes
 * @param netPrice net price in cents
 * @param fees array of fees and taxes
 * @param is_online whether the booking is from an online channel
 * @returns final price, fee amount and tax amount
 */
export function calculateOnNet(
    netPrice: number,
    fees: Array<{ percentage: number; usd: number; is_tax: boolean; online_only?: boolean }>,
    is_online: boolean = false,
) {
    // exception for 0 price items
    if (netPrice === 0) {
        return {
            finalPrice: 0,
            feeAmount: 0,
            taxAmount: 0,
        };
    }

    const debug = [];
    let finalPrice = netPrice;
    let feeAmount = 0;
    let taxAmount = 0;

    debug.push(`Starting net price: ${netPrice}`);
    // first round, fees on the netPrice
    fees.filter((e) => {
        if (!e.is_tax) {
            if (e.online_only && !is_online) {
                return false;
            }
            return true;
        }
        return false;
    }).forEach((fee) => {
        debug.push(`Applying fee: ${JSON.stringify(fee)}`);
        if (fee.percentage !== 0) {
            feeAmount += Math.ceil((netPrice * fee.percentage) / 100);
        }
        if (fee.usd !== 0) {
            feeAmount += fee.usd * 100;
        }
    });
    debug.push(`Total fees amount: ${feeAmount}`);

    // second round, taxes on netPrice + fees
    finalPrice += feeAmount;
    fees.filter((e) => {
        if (e.is_tax) {
            if (e.online_only && !is_online) {
                return false;
            }
            return true;
        }
        return false;
    }).forEach((fee) => {
        debug.push(`Applying tax: ${JSON.stringify(fee)}`);
        if (fee.percentage !== 0) {
            taxAmount += Math.ceil((finalPrice * fee.percentage) / 100);
        }
        if (fee.usd !== 0) {
            taxAmount += fee.usd * 100;
        }
    });
    debug.push(`Total taxes amount: ${taxAmount}`);
    finalPrice += taxAmount;

    // Enable for debugging, if needed
    //console.log(`[calculateOnNet] Final price: ${finalPrice}\nDebug:\n${debug.join('\n')}`);
    return {
        finalPrice,
        feeAmount,
        taxAmount,
    };
}

type CommissionMatrixEntry = { type?: string; value?: unknown };
type CommissionMatrix = Record<string, Record<string, CommissionMatrixEntry>>;

/**
 * What a net-rate agent will actually be charged for a combo: retail price
 * minus the commission group's cut (taxes & fees ignored), mirroring the
 * backend's computeItemCommission + computeNetItemPrice for the no-discount
 * case. Returns null when the matrix has no usable commission for this combo,
 * so callers can fall back to showing the retail price instead of a bogus one.
 */
function computeNetPriceCents(
    priceInCents: number,
    ticketTypeId: number,
    ticketOptionId: number,
    ticketOptionName: string,
    matrix: CommissionMatrix,
): number | null {
    const entry = getMatrixEntry(matrix, ticketTypeId, ticketOptionId, ticketOptionName);
    let commission = 0;
    if (entry?.type === 'fixed') {
        // A fixed commission is stored in dollars; net math is in cents.
        const dollars = Number(entry.value);
        commission = Number.isFinite(dollars) && dollars > 0 ? Math.round(dollars * 100) : 0;
    } else if (entry?.type === 'percentage') {
        const pct = Number(entry.value);
        if (Number.isFinite(pct) && pct > 0) {
            commission = Math.round((priceInCents * pct) / 100);
        }
    }
    if (!(commission > 0 && commission < priceInCents)) {
        return null;
    }
    return priceInCents - commission;
}

type PreviewPriceOptions = {
    netRate?: boolean;
    commissionMatrix?: CommissionMatrix;
    ticketTypeId: number;
    ticketOptionId: number;
    ticketOptionName: string;
    showFees?: Parameters<typeof calculateOnNet>[1];
    isOnline?: boolean;
};

/**
 * Price to DISPLAY for a ticket combo in the modification editor. Under net
 * rate the agent is charged retail minus commission (taxes and fees ignored),
 * so show that; otherwise show retail plus fees/taxes. When a net-rate matrix
 * is misconfigured (computeNetPriceCents returns null and the backend would
 * reject the save) fall back to the bare retail price - never retail+fees,
 * which net rate never charges.
 */
export function previewFinalPriceCents(
    retailPriceInCents: number,
    {
        netRate,
        commissionMatrix,
        ticketTypeId,
        ticketOptionId,
        ticketOptionName,
        showFees,
        isOnline,
    }: PreviewPriceOptions,
): number {
    if (netRate) {
        const net = computeNetPriceCents(
            retailPriceInCents,
            ticketTypeId,
            ticketOptionId,
            ticketOptionName,
            commissionMatrix || {},
        );
        return net ?? retailPriceInCents;
    }
    return calculateOnNet(retailPriceInCents, showFees || [], isOnline).finalPrice;
}

/**
 * previewFinalPriceCents for an editor booking-item row. An existing net-rate
 * item is pinned by the backend to its stored net price (customPriceCents
 * already holds that net), so it is shown as-is rather than netted a second
 * time; a new item's customPriceCents is retail and gets netted.
 */
export function previewItemFinalPriceCents(
    item: {
        oldId?: number | string | null;
        customPriceCents?: number | string;
        ticketTypeId: number;
        ticketOptionId: number;
        ticketOptionName: string;
    },
    opts: Omit<PreviewPriceOptions, 'ticketTypeId' | 'ticketOptionId' | 'ticketOptionName'>,
): number {
    const price = Number(item.customPriceCents) || 0;
    if (opts.netRate && item.oldId) {
        return price;
    }
    return previewFinalPriceCents(price, {
        ...opts,
        ticketTypeId: item.ticketTypeId,
        ticketOptionId: item.ticketOptionId,
        ticketOptionName: item.ticketOptionName,
    });
}

// define price based on ticket type and option
export function definePrice(priceMatrix, ticketOptionName) {
    // if price matrix is empty, return
    if (Object.keys(priceMatrix).length === 0) {
        return;
    }
    const selectedOptionName = ticketOptionName;
    const price = priceMatrix[selectedOptionName];
    // if price is undefined, show error
    if (price === undefined) {
        throw new Error(`Price not found for option: ${selectedOptionName}`);
    }
    return price || 0;
}
